import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../config/api';
import SEO from '../components/SEO';
import './help.css';
import { BRAND } from '../config/brand';
import ContactForm from '../components/ContactForm';

// Human readable labels for section slugs (keep in sync with HelpSection.js)
const SECTION_LABELS = {
  // '3d-tours' : '3D‑туры',
  'registrazia': 'Регистрация',
  'creation': 'Создание',
  // Legacy sections removed from the domain:
  // boards, tags, pins
  'policy': 'Политика',
  'account': 'Аккаунт',
  'community': 'Сообщество',
  'uslugi': 'Услуги и тарифы',
  'support': 'Поддержка',
  'legal': 'Право'
};

const toTitleCase = (s) => (s || '')
  .split('-')
  .map(w => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ');

// Render FAQ answers as safe React nodes with link support.
// Supported syntaxes:
//  1) Plain URL: https://... or www.... (auto-link)
//  2) Labeled link: ["/pages/tarif","здесь"] (URL not shown, only label)
const renderFaqAnswerNodes = (rawText) => {
  const text = String(rawText || '');
  const nodes = [];
  let key = 0;

  const pushText = (chunk) => {
    const parts = String(chunk || '').split('\n');
    parts.forEach((p, idx) => {
      if (p) nodes.push(p);
      if (idx < parts.length - 1) nodes.push(<br key={`br-${key++}`} />);
    });
  };

  const pushLink = (url, label, { isExplicit = false } = {}) => {
    const u = String(url || '').trim();
    const lbl = String(label || u).trim();
    if (!u) {
      pushText(lbl);
      return;
    }

    const isInternal = u.startsWith('/');
    if (isInternal) {
      nodes.push(
        <Link key={`lnk-${key++}`} className="link" to={u}>
          {lbl}
        </Link>
      );
      return;
    }

    const href = u.toLowerCase().startsWith('www.') ? `https://${u}` : u;
    nodes.push(
      <a
        key={`lnk-${key++}`}
        className="link"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {lbl}
      </a>
    );
  };

  // token groups:
  // 1: full ["url","label"] token
  // 2: url within token
  // 3: label within token
  // 4: http(s) url
  // 5: www url
  const tokenRe = /(\[\s*"([^"\n]+)"\s*,\s*"([^"\n]+)"\s*\])|(\bhttps?:\/\/[^\s<]+)|(\bwww\.[^\s<]+)\b/gi;

  let last = 0;
  let m;
  while ((m = tokenRe.exec(text)) !== null) {
    if (m.index > last) pushText(text.slice(last, m.index));

    if (m[1]) {
      // explicit labeled link token
      pushLink(m[2], m[3], { isExplicit: true });
    } else {
      // auto-link
      const url = m[4] || m[5];
      pushLink(url, url);
    }
    last = tokenRe.lastIndex;
  }
  if (last < text.length) pushText(text.slice(last));

  return nodes;
};

export default function HelpPage() {
  const { section, slug } = useParams();
  const fullSlug = `help/${section}/${slug}`;
  const sectionLabel = SECTION_LABELS[String(section || '').toLowerCase()] || toTitleCase(section);

  const { data, error, isLoading } = useQuery(
    ['help-page', section, slug],
    async () => {
      const res = await api.get(`/api/pages/help/${section}/${slug}`);
      return res.data?.page || null;
    },
    { retry: 1 }
  );

  // Resolve related slugs to titles for frontend display
  const [relatedResolved, setRelatedResolved] = useState([]);
  useEffect(() => {
    const slugs = (data && Array.isArray(data.related_slugs)) ? data.related_slugs.filter(Boolean) : [];
    if (slugs.length === 0) { setRelatedResolved([]); return; }

    // Prefer public help endpoint per item to avoid reliance on /resolve routing
    const helpSlugs = slugs.filter(s => String(s || '').startsWith('help/'));
    // const otherSlugs = slugs.filter(s => !String(s || '').startsWith('help/'));

    const toHelpFetch = (s) => {
      const parts = String(s).split('/');
      const section = parts[1];
      const leaf = parts.slice(2).join('/');
      if (!section || !leaf) return Promise.resolve(null);
      return fetch(`/api/pages/help/${encodeURIComponent(section)}/${encodeURIComponent(leaf)}`)
        .then(r => r.ok ? r.json() : null)
        .then(j => j && j.page ? { slug: s, title: j.page.title || s, url: `/${s}` } : { slug: s, title: s, url: `/${s}` })
        .catch(() => ({ slug: s, title: s, url: `/${s}` }));
    };

    Promise.all(helpSlugs.map(toHelpFetch))
      .then(list => {
        const map = new Map((list || []).filter(Boolean).map(p => [p.slug, p]));
        const ordered = slugs.map(s => map.get(s) || { slug: s, title: s, url: `/${s}` });
        setRelatedResolved(ordered);
      })
      .catch(() => {
        // Fallback: slug only
        setRelatedResolved(slugs.map(s => ({ slug: s, title: s, url: `/${s}` })));
      });
  }, [data]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  const notFound = !!error || !data;
  const title = notFound ? toTitleCase(slug) : (data.title || toTitleCase(slug));
  const description = (data && data.meta_description) || `Руководства и ответы на вопросы по ${BRAND.name}.`;
  const canonical = `${BRAND.baseUrl}/help/${encodeURIComponent(section)}/${encodeURIComponent(slug)}`;

  // BreadcrumbList for structured data
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Справка', item: `${BRAND.baseUrl}/help` },
      { '@type': 'ListItem', position: 2, name: sectionLabel, item: `${BRAND.baseUrl}/help/${encodeURIComponent(section)}` },
      { '@type': 'ListItem', position: 3, name: title, item: canonical }
    ]
  };

  const structuredData = [breadcrumbLd];

  // NOTE: HowTo (steps) was removed from the domain.

  // Optional FAQ JSON-LD from data.faq [{ q, a }]
  if (data && Array.isArray(data.faq) && data.faq.length > 0) {
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faq.map(qa => ({
        '@type': 'Question',
        name: qa.q,
        acceptedAnswer: { '@type': 'Answer', text: qa.a }
      }))
    };
    structuredData.push(faqLd);
  }

  const noindex = data && data.indexable === false;
  return (
    <div className="min-h-screen bg-secondary-50">
      <SEO
        title={`${title} | Справка ${BRAND.name}`}
        description={description}
        canonical={canonical}
        url={canonical}
        type="article"
        structuredData={structuredData}
        tags={[section, slug]}
        noindex={!!noindex}
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumbs UI */}
        <nav className="text-sm text-secondary-500 mb-6">
          <Link to="/help" className="text-secondary-800 hover:text-primary-500">Справка</Link>
          <span className="mx-2">|</span>
          <Link to={`/help/${encodeURIComponent(section)}`} className="text-secondary-800 hover:text-primary-500">
            {sectionLabel}</Link>
          <span className="mx-2">|</span>
          <span className="text-secondary-800">{title}</span>
        </nav>

        <h1 className="text-3xl font-bold text-secondary-900 mb-4">{title}</h1>
        {description && (
          <p className="text-secondary-700 mb-8">{description}</p>
        )}
        {data && data.cover_image && (
          <figure className="mb-8">
            <img
              src={data.cover_image}
              alt={title}
              className="w-full h-auto rounded-lg border border-secondary-200 shadow-sm"
              loading="lazy"
              decoding="async"
              sizes="(min-width: 1024px) 768px, 100vw"
            />
          </figure>
        )}

        {notFound ? (
          <div className="prose max-w-none">
            <p>Страница справки пока не опубликована. Ниже — краткая инструкция.</p>
            <h2>Пошаговая инструкция</h2>
            <ol>
              <li>Откройте страницу «Создать пин».</li>
              <li>Загрузите обложку (PNG/JPG до 10 MB).</li>
              <li>Заполните название, описание, выберите доску.</li>
              <li>Добавьте теги (до 10).</li>
              <li>Включите блок «3D‑тур», вставьте ссылку и выберите тип.</li>
              <li>Нажмите «Создать пин».</li>
            </ol>
            <p>
              Нужна помощь? Напишите нам через раздел «Контакты».
            </p>
          </div>
        ) : (
          <article className="help-prose">
            {/* Content from Admin (HTML). Ensure it is sanitized in the Admin before saving. */}
            <div dangerouslySetInnerHTML={{ __html: data.content || '' }} />
          </article>
        )}

        {data?.show_contact_form === true && (
          <ContactForm pageSlug={fullSlug} />
        )}

        {/* FAQ (Accordion) */}
        {data && Array.isArray(data.faq) && data.faq.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">FAQ</h2>
            <div className="rounded-md border border-secondary-200 bg-white divide-y divide-secondary-200">
              {data.faq.map((qa, idx) => (
                <details key={idx} className="p-4">
                  <summary className="cursor-pointer font-semibold text-secondary-900">
                    {qa.q}
                  </summary>
                  <div className="mt-2 text-secondary-800">
                    {renderFaqAnswerNodes(qa.a)}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Related */}
        {(Array.isArray(data?.related_slugs) && data.related_slugs.length > 0) && (
          <div className="mt-12 border-t border-secondary-200 pt-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-3">Связанные материалы</h3>
            <ul className="list-disc list-inside text-secondary-700 space-y-1">
              {(relatedResolved.length > 0 ? relatedResolved : data.related_slugs.map(s => ({ slug: s, title: s, url: `/${s}` }))).map((p) => (
                <li key={p.slug}><Link className="link" to={p.url}>{p.title}</Link></li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
